import React, { useEffect, useState } from "react";
import { getModuleParams } from "../../api/parameters";
import {
  userId,
  userClientDb,
  uiLanguage,
  userDepartmentId,
} from "../../auth/selectors/userSelector";
import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  setUserDepartmentId,
  setUserChangeVersion,
  setUserName,
} from "../../auth/slices/userSlice";
import {
  useLocalStorage,
  useAuthorizationError,
  useLoader,
} from "../../../hooks";
import { Alert } from "../../../components";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "../../../components/form/formUtils";
import { usePreviousState } from "../../../hooks";
import { useSettingsStyles } from "../css";
import { Autocomplete, TextField, Box } from "@mui/material";

function DepartmentList() {
  const { classes } = useSettingsStyles();
  const [translate] = useTranslation();
  const user = useAppSelector(userId);
  const userDb = useAppSelector(userClientDb);
  const userDepartment = useAppSelector(userDepartmentId);
  const [logoutUser] = useAuthorizationError();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<Array<{ value: string; label: string }>>([]);
  const [localStorage, setLocalStorage] = useLocalStorage("departmentId");
  const [error, setError] = useState({ error: false, message: "", status: 0 });
  const [, setLoader] = useLoader();
  const locale = useAppSelector(uiLanguage);
  const previousDb = usePreviousState(userDb ? userDb.id : 0);
  const [selectedDep, setSelectedDep] = useState<{
    label: string;
    value: string;
  } | null>({ value: "", label: "" });

  useEffect(() => {
    const chosen = data ? data.filter((dp) => dp.value === userDepartment) : [];

    setSelectedDep(chosen[0] ?? null);
  }, [data, userDepartment]);

  const params = {
    clientDbId: userDb ? userDb.id : 0,
    locale: locale,
    module: "",
  };

  const assignDepId = React.useCallback(
    (selectedValue: string) => {
      const isUser =
        localStorage && Array.isArray(localStorage)
          ? localStorage.filter(
              (person: any) =>
                person.userId === user && userDb && person.dbId === userDb.id,
            )
          : [];

      if (isUser.length === 0) {
        if (localStorage && localStorage.length > 0) {
          const newStorage = [
            ...localStorage,
            { departmentId: selectedValue, userId: user, dbId: userDb?.id },
          ];
          setLocalStorage(newStorage);
        } else {
          setLocalStorage([
            { departmentId: selectedValue, userId: user, dbId: userDb?.id },
          ]);
        }

        dispatch(setUserDepartmentId(selectedValue));
      } else {
        dispatch(setUserDepartmentId(selectedValue));

        const newStorage = localStorage.filter(
          (person: any) =>
            !(person.userId === user && person.dbId === userDb?.id),
        );
        setLocalStorage([
          ...newStorage,
          { departmentId: selectedValue, userId: user, dbId: userDb?.id },
        ]);
      }
    }, // eslint-disable-next-line
    [dispatch, data, setLocalStorage, userDb],
  );

  const onChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: { value: string; label: string } | null,
  ) => {
    if (value) assignDepId(value.value);
    setSelectedDep(value);
  };

  useEffect(
    () => {
      if (user && userDb && previousDb !== userDb.id) {
        setLoader(true);

        getModuleParams(params)
          .then(({ data }) => {
            setLoader(false);

            const changeVersionButton = data.find(
              (param: any) =>
                param.parameter &&
                param.parameter.toUpperCase() === "CHANGEVERSION",
            );
            if (changeVersionButton && changeVersionButton.code) {
              dispatch(setUserChangeVersion(true));
            }

            const gamaUser = data.find(
              (param: any) =>
                param.parameter &&
                param.parameter.toUpperCase() === "GAMA_USER",
            );
            if (gamaUser && gamaUser.code) {
              dispatch(setUserName(gamaUser.code));
            }

            const departmentsArray = data.filter(
              (dep: any) =>
                dep.parameter && dep.parameter.toUpperCase() === "DEPARTMENTS",
            );

            if (departmentsArray.length === 0) {
              assignDepId("");
              setData([]);
            }

            if (departmentsArray && departmentsArray.length > 0) {
              departmentsArray.unshift({
                code: "",
                name: translate("(none)"),
              });
              setData(
                departmentsArray.map((dep: any) => ({
                  value: "" + dep.code,
                  label: "" + dep.name,
                })),
              );

              let depId = "";
              const isUser =
                localStorage && Array.isArray(localStorage)
                  ? localStorage.filter(
                      (person: any) =>
                        person.userId === user &&
                        userDb &&
                        person.dbId === userDb.id,
                    )
                  : [];

              if (isUser.length > 0) depId = isUser[0].departmentId;
              assignDepId(depId);
            }
          })
          .catch((reason) => {
            setLoader(false);
            const errMessage = getErrorMessage(reason);
            setError({
              error: true,
              message: errMessage.message,
              status: errMessage.status,
            });
          });
      }
    }, // eslint-disable-next-line
    [userDb, translate],
  );

  const handleClose = () => {
    logoutUser(error.status);

    if (error.status !== 401)
      setError({ error: false, message: "", status: 0 });
  };

  const showError = (
    <Alert
      isOpen={error.error}
      message={error.message}
      iconColor="error"
      onClose={handleClose}
    />
  );

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: any;
    },
    option: {
      label?: string;
      value?: string | number;
    },
  ) => (
    <Box component="li" {...props} key={option.value}>
      <b>{option.value + (option.label && option.value ? ": " : "")}</b>
      {option.label}
    </Box>
  );
  return (
    <div className={classes.departmentList}>
      {error.error && showError}
      <Autocomplete
        options={data}
        fullWidth
        disabled={!data || data.length === 0}
        renderOption={renderOption}
        getOptionLabel={(option: any) => option.value + " " + option.label}
        value={data.length > 0 ? selectedDep : null}
        onChange={onChange}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label={translate("departmentcode", "Padalinys")}
            classes={{ root: classes.autocomplete }}
          />
        )}
      />
    </div>
  );
}

export default DepartmentList;
